import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import Box from '@material-ui/core/Box';
import { Button, makeStyles, Paper, Typography } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import api from 'services/api';
import PrintRequest from 'components/PrintRequest';
import { useSnackbar } from 'hooks';

import { getQueryByName } from 'helpers/query';
import { format, parseISO } from 'date-fns';
import { CustomerRequest } from 'components/CustomerRequest';
import { isWaitress } from 'helpers';

const useStyles = makeStyles({
  dialog: {
    padding: 24,
  },
  buttons: {
    display: 'grid',
    gap: 8,
  },
});

const styles = {
  color: '#d90000',
};

function formatDate(createdAt) {
  return format(parseISO(createdAt), 'dd/MM/yyyy HH:mm');
}

function RequestHeader({ request }) {
  return (
    <CustomerRequest.Header
      requestNumber={request.request_number ?? request.id}
    >
      <Typography style={styles}>
        <b>{formatDate(request.created_at)}</b>
      </Typography>
    </CustomerRequest.Header>
  );
}

/**
 * @component
 * @example
 * <TableRequestModal isVisible onClose={() => console.log('Closed!')} />
 */
export default function TableRequestModal({ isVisible = false, onClose }) {
  const classes = useStyles();
  const [request, setRequest] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const { openSnackbar } = useSnackbar();
  const { search } = useLocation();

  useEffect(() => {
    if (!isVisible) return;

    const tableId = getQueryByName(search, 'table');

    if (!tableId) return;

    (async () => {
      try {
        const params = { table_id: tableId };
        const {
          data: [firstRequest],
        } = await api.get('/requests', { params });

        if (!firstRequest) throw new Error();

        const { data } = await api.get(`/requests/${firstRequest.id}`);

        setRequest(data.object);
        setIsLoading(false);
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        handleClose();
        openSnackbar('Esta mesa não possui pedidos.', 'warning');
      }
    })();
  }, [isVisible]);

  const handleClose = () => {
    onClose();
    handleResetRequest();
  };

  const handleResetRequest = () => {
    setRequest();
  };

  return (
    <Dialog open={isVisible && request} onClose={handleClose} fullWidth>
      <Box className={classes.dialog}>
        {!isLoading && !!request && (
          <>
            <CustomerRequest>
              <RequestHeader request={request} />

              <CustomerRequest.Content>
                <CustomerRequest.Info>
                  <CustomerRequest.InfoData>
                    <CustomerRequest.InfoDataLabel>
                      {isWaitress(request.user.profile.id)
                        ? 'Garçom'
                        : 'Cliente'}
                    </CustomerRequest.InfoDataLabel>

                    <CustomerRequest.InfoDataText>
                      {request.user.name}
                    </CustomerRequest.InfoDataText>
                  </CustomerRequest.InfoData>

                  {!isWaitress(request.user.profile.id) && (
                    <CustomerRequest.InfoData>
                      <CustomerRequest.InfoDataLabel>
                        Telefone
                      </CustomerRequest.InfoDataLabel>
                      {request.user.phone.number}
                    </CustomerRequest.InfoData>
                  )}

                  {request.table ? (
                    <CustomerRequest.InfoData>
                      <CustomerRequest.InfoDataLabel>
                        Mesa
                      </CustomerRequest.InfoDataLabel>
                      {request.table.number}
                    </CustomerRequest.InfoData>
                  ) : (
                    <CustomerRequest.InfoData>
                      <CustomerRequest.InfoDataLabel>
                        Para Entrega
                      </CustomerRequest.InfoDataLabel>
                    </CustomerRequest.InfoData>
                  )}
                </CustomerRequest.Info>

                <CustomerRequest.ProductList
                  productList={request.request_products}
                />

                <CustomerRequest.Footer totalPrice={request.total_price} />
              </CustomerRequest.Content>

              <Paper
                component={Box}
                variant="outlined"
                p={1}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <PrintRequest request={request} />

                <Button variant="outlined" onClick={handleClose} fullWidth>
                  Voltar
                </Button>
              </Paper>
            </CustomerRequest>
          </>
        )}
      </Box>
    </Dialog>
  );
}

TableRequestModal.protoTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
